import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Users, Heart, Package, BookOpen, Leaf } from 'lucide-react';

const AboutView = () => {
  return (
    <div className="max-w-4xl mx-auto">

      {/* Hero */}
      <div className="mb-xxxl">
        <h1 className="text-display font-rounded text-primary mb-md">About Nutrius</h1>
        <p className="text-title-3 text-text-secondary leading-relaxed max-w-2xl">
          Nutrius is a nutrition platform built for people who take their dietary needs seriously —
          whether managing allergies, restrictions, or simply trying to eat better for their family.
        </p>
      </div>

      {/* Mission */}
      <div
        className="p-xl rounded-large mb-xxxl"
        style={{ background: 'rgba(var(--color-primary),0.04)', border: '1px solid rgba(var(--color-primary),0.12)' }}
      >
        <div className="flex items-center gap-md mb-md">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Leaf size={20} className="text-primary" />
          </div>
          <h2 className="text-title-2 font-rounded text-primary">Our Mission</h2>
        </div>
        <p className="text-body text-text-secondary leading-relaxed">
          We believe that managing dietary needs should be simple, trusted, and human. Nutrius exists
          to give every household — from individuals with celiac disease to parents managing multiple
          allergies — a platform they can rely on. Every product in our catalog is human-verified.
          Every filter is built to protect, not just organize.
        </p>
      </div>

      {/* What we built */}
      <div className="mb-xxxl">
        <h2 className="text-title-2 font-rounded text-primary mb-lg">What We Built</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-md">
          {[
            { icon: Package,   title: 'Verified Product Catalog',  body: 'Every product is reviewed and verified by our team before it appears in the catalog. No algorithm, no automation — human eyes on every label.' },
            { icon: BookOpen,  title: 'Recipe Discovery',          body: 'Browse community recipes filtered by your exact dietary needs. Find meals your whole household can eat.' },
            { icon: Users,     title: 'Care Profiles',             body: 'Create profiles for everyone in your household. Set their restrictions once, filter everything automatically.' },
            { icon: Shield,    title: 'Trust First',               body: 'We do not use AI to verify dietary claims. We use people. Because your health depends on accuracy, not speed.' },
            { icon: Heart,     title: 'Community',                 body: 'Connect with others navigating the same dietary challenges. Share discoveries, ask questions, find support.' },
            { icon: Leaf,      title: 'Grocery Integration',       body: 'Build grocery lists from verified products and recipes. Shop with confidence knowing every item fits your needs.' },
          ].map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="p-lg rounded-large bg-surface"
              style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
            >
              <div className="flex items-center gap-sm mb-sm">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon size={16} className="text-primary" />
                </div>
                <h3 className="text-callout font-semibold text-text-primary">{title}</h3>
              </div>
              <p className="text-caption text-text-secondary leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Company */}
      <div
        className="p-xl rounded-large mb-xxxl"
        style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
      >
        <h2 className="text-title-2 font-rounded text-primary mb-md">The Company</h2>
        <p className="text-body text-text-secondary leading-relaxed mb-md">
          Nutrius is the first product from <strong className="text-text-primary">HealthWealth LLC</strong>,
          a company focused on building tools that help people make better decisions about their health.
          We started with nutrition because we experienced firsthand how difficult it is to navigate
          dietary restrictions without a reliable source of truth.
        </p>
        <p className="text-body text-text-secondary leading-relaxed">
          We are a small team building in public, growing with our users, and committed to keeping
          the platform honest, transparent, and free of misleading data.
        </p>
      </div>

      {/* CTA */}
      <div className="text-center pb-xl">
        <p className="text-callout text-text-secondary mb-lg">
          Have questions or want to get in touch?
        </p>
        <div className="flex gap-md justify-center flex-wrap">
          <Link
            to="/contact"
            className="inline-flex items-center gap-sm px-xl py-md bg-primary text-white rounded-large text-callout font-semibold hover:bg-primary/90 transition-all"
          >
            Contact Us
          </Link>
          <Link
            to="/products"
            className="inline-flex items-center gap-sm px-xl py-md bg-surface border border-text-tertiary/20 text-text-primary rounded-large text-callout font-semibold hover:border-primary/30 transition-all"
          >
            Browse Products
          </Link>
        </div>
      </div>

    </div>
  );
};

export default AboutView;
